import { inject, Injectable } from '@angular/core';
import {
  CollectionFilterRequestBuilder,
  CollectionRequestBuilder,
} from '@web/common';
import { WarehouseProductApiService } from '../api/services/warehouse-product-api.service';
import {
  FileSaverService,
  TableDataPayload,
  TableLazyLoad,
} from '@web/shared/ui';
import { map, Observable, switchMap } from 'rxjs';
import { dateFormat, executeOrNull, Guid } from '@web/utils';
import {
  WarehouseProductCollectionFilter,
  WarehouseProductCollectionItem,
  WarehouseProductDetails,
  WarehouseProductFormData,
  WarehouseProductQuantityHistoryCollectionFilter,
  WarehouseProductQuantityHistoryCollectionItem,
  WarehouseProductQuantityHistoryReceipt,
  WarehouseProductQuantityHistoryRelease,
  WarehouseProductQuantityHistoryRestock,
  WarehouseSkuUsage,
  WarehouseSkuUsageProduct,
  WarehouseSkuUsageReceipt,
} from '../models';
import { WarehouseProductUpdateRequest } from '../api/requests/warehouse-product-update.request';

@Injectable({
  providedIn: 'root',
})
export class WarehouseProductDataSourceService {
  private readonly warehouseProductApi = inject(WarehouseProductApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly fileSaver = inject(FileSaverService);

  public fetchCollection(
    owner: string,
    lazyLoad: TableLazyLoad<WarehouseProductCollectionFilter>,
  ): Observable<TableDataPayload<WarehouseProductCollectionItem>> {
    return this.warehouseProductApi
      .fetchCollection({
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            /* prettier-ignore */
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendBoolean((builder) => builder.equalFor('promotion').build())
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.equalFor('quantityUnit').build())
              .appendText((builder) => builder.equalFor('type').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('brand').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('durabilityInformation').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('localisation').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('measure').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('name').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('owner').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('quantity').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('sku').build())
              .buildAsArray(),
          )
          .filters(
            (
              builder: CollectionFilterRequestBuilder<WarehouseProductCollectionFilter>,
            ) =>
              builder
                .appendText((builder) =>
                  builder.equalFor('owner').value(owner).build(),
                )
                .build(),
          )
          .build(),
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload<WarehouseProductCollectionItem>(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new WarehouseProductCollectionItem({
                    id: item.id,
                    brand: item.brand.label,
                    brandId: item.brand.id,
                    durabilityInformation: item.durabilityInformation,
                    measure: item.measure,
                    promotion: item.promotion,
                    quantity: item.quantity,
                    quantityUnit: item.quantityUnit,
                    name: item.name,
                    sku: item.sku,
                    type: item.type,
                    createdAt: item.createdAt,
                    owner: item.owner.label,
                    ownerId: item.owner.id,
                    localization: item.localisation?.label,
                    localizationId: item.localisation?.id,
                    canBeViewed: item.contextOperations.canBeViewed,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeLabelDownloaded: item.contextOperations.canBeLabelDownloaded,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchQuantityHistoryCollection(params: {
    lazyLoad: TableLazyLoad<WarehouseProductQuantityHistoryCollectionFilter>;
    owner: string;
    id: Guid;
  }): Observable<
    TableDataPayload<WarehouseProductQuantityHistoryCollectionItem>
  > {
    const { lazyLoad, owner, id } = params;

    return this.warehouseProductApi
      .fetchQuantityHistoryCollection({
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            /* prettier-ignore */
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.equalFor('change').build())
              .appendText((builder) => builder.equalFor('release').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('restock').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('receipt').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('reason').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('createdBy').build())
              .buildAsArray(),
          )
          .build(),
        owner,
        id,
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload<WarehouseProductQuantityHistoryCollectionItem>(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new WarehouseProductQuantityHistoryCollectionItem({
                    id: item.id,
                    change: item.change,
                    quantityBeforeChange: item.quantityBeforeChange,
                    quantityAfterChange: item.quantityAfterChange,
                    createdAt: item.createdAt,
                    createdBy: item.createdBy,
                    reason: item.reason,
                    receipt: executeOrNull(item.receipt, (receipt) => new WarehouseProductQuantityHistoryReceipt({
                      id: receipt.id,
                      number: receipt.number,
                    })),
                    release: executeOrNull(item.release, (release) => new WarehouseProductQuantityHistoryRelease({
                      id: release.id,
                      number: release.number,
                    })),
                    restock: executeOrNull(item.restock, (restock) => new WarehouseProductQuantityHistoryRestock({
                      id: restock.id,
                      number: restock.number,
                    })),
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsById(
    owner: string,
    id: Guid,
    cacheable = false,
  ): Observable<WarehouseProductDetails> {
    return this.warehouseProductApi.fetchDetailsById(owner, id, cacheable).pipe(
      map(
        (response) =>
          /* prettier-ignore */
          new WarehouseProductDetails({
            id: response.id,
            brand: response.brand.label,
            brandId: response.brand.id,
            durabilityInformation: response.durabilityInformation,
            measure: response.measure,
            promotion: response.promotion,
            quantity: response.quantity,
            quantityUnit: response.quantityUnit,
            name: response.name,
            sku: response.sku,
            type: response.type,
            createdAt: response.createdAt,
            owner: response.owner.label,
            ownerId: response.owner.id,
            localization: response.localisation?.label,
            localizationId: response.localisation?.id,
            canBeViewed: response.contextOperations.canBeViewed,
            canBeEdited: response.contextOperations.canBeEdited,
            canBeLabelDownloaded: response.contextOperations.canBeLabelDownloaded,
            canBeQuantityEdited: response.contextOperations.canBeQuantityEdited,
          }),
      ),
    );
  }

  public updateByFormData(
    owner: string,
    id: Guid,
    data: WarehouseProductFormData,
  ): Observable<void> {
    return this.warehouseProductApi.update(
      owner,
      id,
      new WarehouseProductUpdateRequest(
        data.type!,
        data.quantity!,
        data.unit!,
        data.sku!,
        data.name!,
        data.ownerId!,
        data.brandId!,
        data.localizationId!,
        data.promotion!,
        data.durabilityInformation!,
        data.measure!,
      ),
    );
  }

  public fetchDetailsFormById(
    owner: string,
    id: Guid,
  ): Observable<WarehouseProductFormData> {
    return this.warehouseProductApi.fetchDetailsById(owner, id).pipe(
      map(
        (response) =>
          new WarehouseProductFormData({
            brandId: response.brand.id,
            durabilityInformation: response.durabilityInformation,
            measure: response.measure,
            promotion: response.promotion,
            quantity: response.quantity,
            unit: response.quantityUnit,
            name: response.name,
            sku: response.sku,
            type: response.type,
            ownerId: response.owner.id,
            localizationId: response.localisation?.id,
          }),
      ),
    );
  }

  /* prettier-ignore */
  public fetchSkuUsage(sku: string): Observable<WarehouseSkuUsage> {
    return this.warehouseProductApi.fetchSkuUsage(sku).pipe(
      map((response) => new WarehouseSkuUsage({
        status: response.status,
        product: response.good && new WarehouseSkuUsageProduct({
          id: response.good.id,
          name: response.good.name,
        }),
        receipts: response.receipts && response.receipts.map((receipt) => new WarehouseSkuUsageReceipt({
          id: receipt.id,
          number: receipt.number,
        })),
      })),
    );
  }

  public fetchProductLabel(owner: string, id: Guid): Observable<void> {
    return this.warehouseProductApi
      .fetchDetailsById(owner, id)
      .pipe(
        switchMap((response) =>
          this.warehouseProductApi
            .fetchProductLabel(owner, id)
            .pipe(
              switchMap((blob) => this.fileSaver.saveAs(blob, response.sku)),
            ),
        ),
      );
  }

  public fetchQuantityReport(owner: string): Observable<void> {
    const name = `raport_stany_magazynowe_${owner}_${dateFormat(new Date(), 'DD-MM-YYYY')}`;

    return this.warehouseProductApi
      .fetchQuantityReport(owner)
      .pipe(switchMap((blob) => this.fileSaver.saveAs(blob, name)));
  }
}
